import React, { useEffect, useRef, useState } from "react";
import { CalendarDays, X, Flame, Check, Star } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

export default function DailyRewards({ profileId, ready = true, onReward }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(null);
  const checkedRef = useRef(false);
  const tz = React.useMemo(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return undefined; }
  }, []);

  // Auto check-in once per page session — but only after profiles have loaded, so the
  // reward can be banked onto the selected account (not lost while profileId is still null).
  useEffect(() => {
    if (!ready || checkedRef.current) return;
    checkedRef.current = true;
    api
      .post("/me/checkin", { profile_id: profileId || undefined, tz })
      .then(({ data }) => {
        setState(data);
        if (data.milestone_hit) {
          toast.success(data.message, { icon: "🎁", duration: 9000 });
          onReward?.();
        } else if (data.awarded_points > 0) {
          toast(data.message, { icon: "✅", duration: 4500 });
          onReward?.();
        }
      })
      .catch(() => {
        api.get("/me/checkin", { params: { tz } }).then(({ data }) => setState(data)).catch(() => {});
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, profileId]);

  const refresh = () =>
    api.get("/me/checkin", { params: { tz } }).then(({ data }) => setState(data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));

  useEffect(() => {
    if (open && !state) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const streak = state?.streak ?? 0;
  const days = state?.days_this_month || [];
  const today = state?.today;

  const now = new Date();
  const year = now.getFullYear();
  const monthIdx = now.getMonth();
  const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
  const firstWeekday = new Date(year, monthIdx, 1).getDay();
  const cells = [...Array(firstWeekday).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  return (
    <>
      <button data-testid="open-daily-rewards" onClick={() => setOpen(true)} className="osrs-btn px-3 py-2 text-sm flex items-center gap-2" title="Daily login rewards">
        <CalendarDays size={16} className="text-[#FF9A3A]" />
        <span>Daily</span>
        {streak > 0 && (
          <span className="inline-flex items-center gap-0.5 osrs-num text-[#FF7A1A] text-xs" data-testid="daily-streak-badge">
            <Flame size={12} /> {streak}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" onClick={() => setOpen(false)}>
          <div className="osrs-panel p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="daily-rewards-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="osrs-title text-xl flex items-center gap-2"><CalendarDays size={20} className="text-[#FF9A3A]" /> Daily rewards</h3>
              <button onClick={() => setOpen(false)} className="osrs-btn px-2 py-1" data-testid="close-daily-rewards"><X size={16} /></button>
            </div>

            <div className="flex items-center gap-3 mb-4 px-3 py-2 border-2 border-black bg-[#241b10]" data-testid="daily-streak-panel">
              <Flame size={22} className={streak > 0 ? "text-[#FF7A1A]" : "text-[#7a6a45]"} style={{ filter: "drop-shadow(1px 1px 0 #000)" }} />
              <div className="flex-1 min-w-0">
                <p className="osrs-shadow text-sm text-[#E6A519]">
                  Login streak: <span className="osrs-num text-[#FFCF3F]">{streak}</span> day{streak === 1 ? "" : "s"}
                </p>
                <p className="osrs-shadow text-[11px] text-[#b9a06a]">
                  <span className="text-[#7CFC7C]">+{state?.daily_points ?? 1} pt</span> daily ·
                  {" "}<span className="osrs-num">{state?.next_milestone_in ?? 7}</span> day{(state?.next_milestone_in ?? 7) === 1 ? "" : "s"} → <span className="text-[#FFCF3F]">+{state?.next_milestone_reward ?? 10} pts</span> milestone
                </p>
              </div>
              {state?.checked_in_today && (
                <span className="osrs-num text-[11px] text-[#7CFC7C] flex items-center gap-1 shrink-0" data-testid="checked-in-today"><Check size={13} /> today</span>
              )}
            </div>

            <p className="osrs-shadow text-[11px] text-[#b9a06a] uppercase text-center mb-2">{MONTH_NAMES[monthIdx]} {year}</p>
            <div className="grid grid-cols-7 gap-1 mb-3" data-testid="daily-calendar">
              {WEEKDAYS.map((d, i) => (
                <div key={`h${i}`} className="text-center osrs-shadow text-[10px] text-[#8a7a54] py-1">{d}</div>
              ))}
              {cells.map((day, i) => {
                if (day === null) return <div key={`e${i}`} />;
                const done = days.includes(day);
                const isToday = day === today;
                const milestone = done && day % 7 === 0; // visual accent only
                return (
                  <div
                    key={day}
                    data-testid={`cal-day-${day}`}
                    className={`aspect-square flex items-center justify-center border text-[11px] osrs-num relative
                      ${done ? "bg-[#3a7d1e] text-white border-[#5aa832]" : "bg-black/30 text-[#8a7a54] border-[#0f0d09]"}
                      ${isToday ? "ring-2 ring-[#FFCF3F]" : ""}`}
                  >
                    {done ? <Check size={12} /> : day}
                    {milestone && <Star size={8} className="absolute top-0.5 right-0.5 text-[#FFCF3F]" />}
                  </div>
                );
              })}
            </div>
            <p className="osrs-shadow text-[11px] text-[#b9a06a] text-center leading-snug">
              Check in daily for <span className="text-[#7CFC7C]">+{state?.daily_points ?? 1} point</span>. Every <span className="text-[#FFCF3F]">7-day</span> streak banks a bigger <span className="text-[#FFCF3F]">escalating bonus</span> (+10, +15, +20…) onto your account. Miss a day and the streak resets.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
